import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/**
 * EngagingLoaderComponent displays user-engaging loader with thinking phase and bullet points
 * Bullet points blink and hide one by one, last one stays until streaming starts
 * Each bullet point is randomly selected from 5 bullet options per bullet point
 */
@Component({
  selector: 'app-engaging-loader',
  templateUrl: './engaging-loader.component.html',
  styleUrls: ['./engaging-loader.component.css']
})
export class EngagingLoaderComponent implements OnInit, OnDestroy, OnChanges {
  @Input() showStreamingDots: boolean = false; // Show three-dot loader during streaming
  private currentModule: string = 'regulations';
  
  @Input() 
  set module(value: string) {
    const newModule = value?.toLowerCase() || 'regulations';
    if (this.currentModule !== newModule) {
      this.currentModule = newModule;
      this.onModuleChange();
    }
  }
  
  get module(): string {
    return this.currentModule;
  }
  
  itemStates: ('visible' | 'blinking' | 'hidden')[] = ['blinking', 'visible', 'visible', 'visible', 'visible'];
  
  // Randomized bullet point texts for each phase
  bulletPointTexts: string[] = ['', '', '', '', ''];
  
  // Translation keys for Regulations module
  public readonly regulationsBulletKeys: { [key: string]: string[] } = {
    bullet1: [
      'ENGAGING_LOADER.BULLET1_OPTION1',
      'ENGAGING_LOADER.BULLET1_OPTION2',
      'ENGAGING_LOADER.BULLET1_OPTION3',
    ],
    bullet2: [
      'ENGAGING_LOADER.BULLET2_OPTION1',
      'ENGAGING_LOADER.BULLET2_OPTION2',
      'ENGAGING_LOADER.BULLET2_OPTION3',
    ],
    bullet3: [
      'ENGAGING_LOADER.BULLET3_OPTION1',
      'ENGAGING_LOADER.BULLET3_OPTION2',
      'ENGAGING_LOADER.BULLET3_OPTION3',
      'ENGAGING_LOADER.BULLET3_OPTION4',
    ]
  };
  
  // RTQ Module - Set 1 (5 bullets)
  public readonly rtqSet1Keys: string[] = [
    'ENGAGING_LOADER.RTQ_SET1_BULLET1',
    'ENGAGING_LOADER.RTQ_SET1_BULLET2',
    'ENGAGING_LOADER.RTQ_SET1_BULLET3',
    'ENGAGING_LOADER.RTQ_SET1_BULLET4',
    'ENGAGING_LOADER.RTQ_SET1_BULLET5',
  ];
  
  // RTQ Module - Set 2 (5 bullets)
  public readonly rtqSet2Keys: string[] = [
    'ENGAGING_LOADER.RTQ_SET2_BULLET1',
    'ENGAGING_LOADER.RTQ_SET2_BULLET2',
    'ENGAGING_LOADER.RTQ_SET2_BULLET3',
    'ENGAGING_LOADER.RTQ_SET2_BULLET4',
    'ENGAGING_LOADER.RTQ_SET2_BULLET5',
  ];
  
  public itemTimeouts: any[] = [];
  public selectedRTQSet: number = 1; // Track which RTQ set is selected (1 or 2)
  public bulletCount: number = 3; // Number of bullets to show (3 for Regulations, 5 for RTQ)

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService
  ) {
    // Randomize bullet points on component creation
    this.randomizeBulletPoints();
    
    // Re-randomize when language changes
    this.translate.onLangChange.subscribe(() => {
      this.randomizeBulletPoints();
    });
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    // Module changes are now handled by the setter
  }
  
  /**
   * Called when module changes to reinitialize the loader
   */
  private onModuleChange(): void {
    // Re-randomize bullet points for the new module
    this.randomizeBulletPoints();
    
    // If component is already initialized, reset and restart
    if (this.itemTimeouts.length > 0) {
      this.itemTimeouts.forEach(timeout => clearTimeout(timeout));
      this.itemTimeouts = [];
      this.resetItemStates();
      this.startSequence();
    }
    
    // Trigger change detection to update the view
    this.cdr.detectChanges();
  }
  
  /**
   * Resets item states to initial configuration
   */
  private resetItemStates(): void {
    this.itemStates = ['blinking', 'visible', 'visible', 'visible', 'visible'];
  }
  
  /**
   * Randomly selects one option from each bullet's translation keys and fetches the translated text
   */
  private randomizeBulletPoints(): void {
    const moduleLower = this.module?.toLowerCase() || 'regulations';
    
    if (moduleLower === 'rtq') {
      // Randomly select between Set 1 (5 bullets) and Set 2 (5 bullets)
      this.selectedRTQSet = Math.floor(Math.random() * 2) + 1; // NOSONAR: Safe - used for UI randomization only, not security
      const selectedKeys = this.selectedRTQSet === 1 ? this.rtqSet1Keys : this.rtqSet2Keys;
      this.bulletCount = selectedKeys.length;
      
      // Load the selected set's bullet points
      selectedKeys.forEach((key, index) => {
        this.bulletPointTexts[index] = this.translate.instant(key);
      });
    } else {
      // Default to Regulations module (random selection from options)
      this.bulletCount = 3;
      const bullets = Object.keys(this.regulationsBulletKeys);
      bullets.forEach((bullet, index) => {
        const keys = this.regulationsBulletKeys[bullet];
        const randomKey = keys[Math.floor(Math.random() * keys.length)]; // NOSONAR: Safe - used for UI randomization only, not security
        this.bulletPointTexts[index] = this.translate.instant(randomKey);
      });
    }
  }

  ngOnInit(): void {
    // Start the blinking and hiding sequence
    this.startSequence();
  }

  private startSequence(): void {
    // Item 0: Already blinking, hide after 8 seconds
    this.itemTimeouts.push(setTimeout(() => {
      this.itemStates[0] = 'hidden';
    }, 8000));
  
    // Item 1: Start blinking after 8 seconds, hide after 16 seconds total
    this.itemTimeouts.push(setTimeout(() => {
      this.itemStates[1] = 'blinking';
    }, 8000));
    this.itemTimeouts.push(setTimeout(() => {
      this.itemStates[1] = 'hidden';
    }, 16000));
  
    // Item 2: Start blinking after 16 seconds, stays visible until streaming starts
    this.itemTimeouts.push(setTimeout(() => {
      this.itemStates[2] = 'blinking';
    }, 16000));
    // Last bullet stays visible - will be hidden when showStreamingDots becomes true
  }

  get visibleItemsCount(): number {
    return this.itemStates.filter(state => state !== 'hidden').length;
  }

  ngOnDestroy(): void {
    this.itemTimeouts.forEach(timeout => clearTimeout(timeout));
  }
}
